import m from 'mithril'
import { css } from '@emotion/css'
import { colors, font, space, btnPrimary, btnSecondary } from '../styles/theme.js'

// --- Styles ---

const panelWrap = css`
  font-size: ${font.size.sm};
`

const filterRow = css`
  display: flex;
  gap: ${space.xs};
  margin-bottom: ${space.sm};
  flex-wrap: wrap;
`

const filterBtn = css`
  padding: 2px 8px;
  border-radius: 4px;
  font-size: ${font.size.xs};
  font-weight: ${font.weight.medium};
  border: 1px solid ${colors.border};
  background: ${colors.bgCard};
  color: ${colors.textSecondary};
  cursor: pointer;
  &:hover { background: ${colors.bg}; }
`

const filterBtnActive = css`
  ${filterBtn};
  background: ${colors.brand};
  color: white;
  border-color: ${colors.brand};
  &:hover { background: ${colors.brandDark}; }
`

const commentList = css`
  max-height: 300px;
  overflow-y: auto;
`

const commentItem = css`
  padding: ${space.sm} 0;
  border-bottom: 1px solid ${colors.border};
  &:last-child { border-bottom: none; }
`

const commentReply = css`
  margin-left: ${space.md};
  padding-left: ${space.sm};
  border-left: 2px solid ${colors.border};
`

const commentHeader = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2px;
`

const commentUser = css`
  font-weight: ${font.weight.medium};
  font-size: ${font.size.xs};
  color: ${colors.text};
`

const commentTime = css`
  font-size: ${font.size.xs};
  color: ${colors.textMuted};
`

const commentContent = css`
  line-height: 1.5;
  white-space: pre-wrap;
  word-wrap: break-word;
`

const commentActions = css`
  display: flex;
  gap: ${space.sm};
  margin-top: 2px;
`

const actionBtn = css`
  font-size: ${font.size.xs};
  color: ${colors.textMuted};
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  &:hover { color: ${colors.brand}; }
`

const highlightBadge = css`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: ${font.size.xs};
  padding: 1px 6px;
  border-radius: 3px;
  margin-bottom: 2px;
`

const formWrap = css`
  margin-top: ${space.sm};
  padding-top: ${space.sm};
  border-top: 1px solid ${colors.border};
`

const textarea = css`
  width: 100%;
  padding: ${space.xs} ${space.sm};
  border: 1px solid ${colors.border};
  border-radius: 4px;
  font-size: ${font.size.xs};
  font-family: ${font.family};
  resize: vertical;
  min-height: 48px;
  outline: none;
  &:focus { border-color: ${colors.borderFocus}; }
`

const formActions = css`
  display: flex;
  gap: ${space.xs};
  justify-content: flex-end;
  margin-top: ${space.xs};
`

const submitBtn = css`
  ${btnPrimary};
  font-size: ${font.size.xs};
  padding: 3px ${space.sm};
`

const cancelBtn = css`
  ${btnSecondary};
  font-size: ${font.size.xs};
  padding: 3px ${space.sm};
`

const emptyStyle = css`
  font-size: ${font.size.xs};
  color: ${colors.textMuted};
  font-style: italic;
  padding: ${space.sm} 0;
`

// --- Helpers ---

// Build a tree from flat comment list for threading
export function buildCommentTree(comments) {
  const map = new Map()
  const roots = []

  for (const c of comments) {
    map.set(c.id, { ...c, children: [] })
  }

  for (const c of comments) {
    const node = map.get(c.id)
    if (c.parent_id && map.has(c.parent_id)) {
      map.get(c.parent_id).children.push(node)
    } else {
      roots.push(node)
    }
  }

  return roots
}

// Filter comments for a specific highlight or session-wide
export function filterComments(comments, highlightId) {
  if (!highlightId) return comments // show all
  return comments.filter(c => c.highlight_id === highlightId)
}

function formatTime(isoString) {
  if (!isoString) return ''
  try {
    const d = new Date(isoString)
    return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
  } catch { return isoString }
}

// --- Component ---

// CommentsPanel
// Props:
//   comments        - array of comment objects
//   highlights      - array of highlight objects (for badge display)
//   activeHighlight - highlight_id to filter by, or null for all
//   onPost          - (content, highlightId, parentId) => void
//   onDelete        - (commentId) => void
//   onFilterChange  - (highlightId | null) => void
export function CommentsPanel() {
  let newContent = ''
  let replyTo = null

  function resetForm() {
    newContent = ''
    replyTo = null
  }

  function renderComment(node, attrs, depth) {
    const { onPost, onDelete } = attrs
    const isReply = depth > 0
    const hl = node.highlight_id
      ? (attrs.highlights || []).find(h => h.id === node.highlight_id)
      : null

    // Build children imperatively to avoid mixing keyed and non-keyed vnodes.
    // Recursive renderComment() returns keyed vnodes (key: node.id), so they
    // must not be siblings of non-keyed elements or null holes.
    const parts = []

    // Highlight badge (only for top-level comments anchored to a highlight)
    if (!isReply && hl) {
      parts.push(m('div', {
        class: highlightBadge,
        style: `background: ${hl.type_color}22; color: ${hl.type_color};`,
      }, [
        m('span', { style: `width:6px;height:6px;border-radius:50%;background:${hl.type_color};` }),
        hl.type_name,
      ]))
    }

    parts.push(m('div', { class: commentHeader }, [
      m('span', { class: commentUser }, (attrs.memberMap || {})[node.user_id] || 'User'),
      m('span', { class: commentTime }, formatTime(node.created_at)),
    ]))

    parts.push(m('div', { class: commentContent }, node.content))

    const actionBtns = [
      m('button', {
        class: actionBtn,
        onclick: () => { replyTo = node.id },
      }, 'Reply'),
    ]
    if (onDelete) {
      actionBtns.push(m('button', {
        class: actionBtn,
        onclick: () => onDelete(node.id),
      }, 'Delete'))
    }
    parts.push(m('div', { class: commentActions }, actionBtns))

    // Reply form
    if (replyTo === node.id) {
      parts.push(m('div', { style: `margin-top: ${space.xs};` }, [
        m('textarea', {
          class: textarea,
          placeholder: 'Write a reply...',
          value: newContent,
          oninput: (e) => { newContent = e.target.value },
          rows: 2,
        }),
        m('div', { class: formActions }, [
          m('button', {
            class: cancelBtn,
            onclick: resetForm,
          }, 'Cancel'),
          m('button', {
            class: submitBtn,
            disabled: !newContent.trim(),
            onclick: () => {
              if (newContent.trim()) {
                onPost(newContent.trim(), node.highlight_id || null, node.id)
                resetForm()
              }
            },
          }, 'Reply'),
        ]),
      ]))
    }

    // Children (replies) — wrapped in a container so keyed reply vnodes
    // are not siblings of the non-keyed elements above.
    const childComments = (node.children || []).map(child =>
      renderComment(child, attrs, depth + 1)
    )
    if (childComments.length > 0) {
      parts.push(m('div', childComments))
    }

    return m('div', {
      key: node.id,
      class: isReply ? commentReply : commentItem,
    }, parts)
  }

  return {
    view(vnode) {
      const {
        comments = [], highlights = [],
        activeHighlight, onPost, onDelete, onFilterChange,
      } = vnode.attrs

      const filtered = filterComments(comments, activeHighlight)
      const tree = buildCommentTree(filtered)

      // Unique highlights with comments
      const hlsWithComments = [...new Set(comments
        .filter(c => c.highlight_id)
        .map(c => c.highlight_id)
      )]

      return m('div', { class: panelWrap }, [
        // Filter buttons
        (hlsWithComments.length > 0 || activeHighlight)
          ? m('div', { class: filterRow }, [
              m('button', {
                key: '__all__',
                class: !activeHighlight ? filterBtnActive : filterBtn,
                onclick: () => onFilterChange && onFilterChange(null),
              }, 'All'),
              ...hlsWithComments.map(hlId => {
                const hl = highlights.find(h => h.id === hlId)
                return m('button', {
                  key: hlId,
                  class: activeHighlight === hlId ? filterBtnActive : filterBtn,
                  onclick: () => onFilterChange && onFilterChange(hlId),
                  style: activeHighlight === hlId ? '' : `border-color: ${hl?.type_color || colors.border};`,
                }, hl?.type_name || 'Highlight')
              }),
            ])
          : null,

        // Comment list
        tree.length > 0
          ? m('div', { class: commentList },
              tree.map(node => renderComment(node, vnode.attrs, 0)))
          : m('div', { class: emptyStyle }, 'No comments yet.'),

        // New comment form (only when not replying to a specific comment)
        replyTo === null
          ? m('div', { class: formWrap }, [
              m('textarea', {
                class: textarea,
                placeholder: activeHighlight
                  ? 'Comment on this highlight...'
                  : 'Add a comment...',
                value: newContent,
                oninput: (e) => { newContent = e.target.value },
                rows: 2,
              }),
              m('div', { class: formActions }, [
                m('button', {
                  class: submitBtn,
                  disabled: !newContent.trim(),
                  onclick: () => {
                    if (newContent.trim() && onPost) {
                      onPost(newContent.trim(), activeHighlight || null, null)
                      newContent = ''
                    }
                  },
                }, 'Post'),
              ]),
            ])
          : null,
      ])
    },
  }
}
